import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Fade, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import useTeamColors from '../../hooks/useTeamColors';
import { getTeamForPlayer } from '../../game/teamUtils';
import { EMOTE_PRESETS } from './chatPresets';

/**
 * One message bubble, WhatsApp-style: own messages lean right in the familiar
 * dark green, everyone else's lean left in dark grey with their name (colored
 * by team, so partner vs. rival is still obvious at a glance) shown above.
 * Bubbles are solid/opaque on purpose — unlike the old translucent tint,
 * that keeps them equally legible whether they sit on the floating in-game
 * overlay's dark backdrop or the waiting room's light Card.
 */
function MessageBubble({ message, isMine, teamColors, t }) {
    const team = message.senderSeat !== null && message.senderSeat !== undefined ? getTeamForPlayer(message.senderSeat) : null;
    const nameColor = team === 1 ? teamColors.team1.main : team === 2 ? teamColors.team2.main : '#8696a0';
    const text = message.kind === 'emote' ? t(`online.chat.emotes.${message.body}`, message.body) : message.body;
    const emoji = message.kind === 'emote' ? EMOTE_PRESETS.find((e) => e.slug === message.body)?.emoji : null;
    const time = new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start', mb: 0.75 }}>
            {!isMine && message.senderName && (
                <Typography sx={{ fontSize: '0.65rem', color: nameColor, fontWeight: 'bold', mx: 0.5 }}>
                    {message.senderName}
                </Typography>
            )}
            <Box
                sx={{
                    maxWidth: '85%',
                    px: 1.25,
                    py: 0.6,
                    borderRadius: isMine ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    backgroundColor: isMine ? '#005c4b' : '#202c33',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    opacity: message.pending ? 0.6 : 1,
                    wordBreak: 'break-word',
                }}
            >
                <Typography sx={{ color: '#e9edef', fontSize: '0.85rem' }}>
                    {emoji ? `${emoji} ` : ''}
                    {text}
                </Typography>
                <Typography sx={{ color: 'rgba(233,237,239,0.6)', fontSize: '0.6rem', textAlign: 'right', mt: 0.25 }}>
                    {time}
                </Typography>
            </Box>
        </Box>
    );
}

/** Shared list + emote row + input, used verbatim by both variants below. `dark` picks
 * legible input colors for the variant's background — floating sits on a near-black
 * overlay, inline sits inside a near-white MuiCard. */
function ChatBody({ messages, onSend, myUserId, disabled, height, grow = false, dark = true }) {
    const { t } = useTranslation();
    const { teamColors } = useTeamColors();
    const [draft, setDraft] = useState('');
    const listRef = useRef(null);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages.length]);

    const handleSend = () => {
        const trimmed = draft.trim();
        if (!trimmed || disabled) return;
        onSend('text', trimmed);
        setDraft('');
    };

    return (
        <>
            <Box
                ref={listRef}
                sx={grow ? { flex: 1, minHeight: 0, overflowY: 'auto', px: 0.5, mb: 1 } : { height, overflowY: 'auto', px: 0.5, mb: 1 }}
            >
                {messages.length === 0 ? (
                    <Typography
                        sx={{
                            color: dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
                            fontSize: '0.8rem',
                            textAlign: 'center',
                            mt: 2,
                        }}
                    >
                        {t('online.chat.empty')}
                    </Typography>
                ) : (
                    messages.map((message) => (
                        <MessageBubble key={message.id} message={message} isMine={message.senderUserId === myUserId} teamColors={teamColors} t={t} />
                    ))
                )}
            </Box>

            <Stack direction="row" spacing={0.5} sx={{ overflowX: 'auto', pb: 0.75 }}>
                {EMOTE_PRESETS.map((preset) => (
                    <Tooltip key={preset.slug} title={t(`online.chat.emotes.${preset.slug}`)}>
                        <Chip
                            label={preset.emoji}
                            size="small"
                            onClick={() => !disabled && onSend('emote', preset.slug)}
                            sx={{ cursor: disabled ? 'default' : 'pointer', backgroundColor: 'rgba(255,255,255,0.1)', flexShrink: 0 }}
                        />
                    </Tooltip>
                ))}
            </Stack>

            <Stack direction="row" spacing={1}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder={t('online.chat.placeholder')}
                    value={draft}
                    disabled={disabled}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSend();
                    }}
                    inputProps={{ maxLength: 300 }}
                    sx={{
                        '& .MuiInputBase-root': {
                            color: dark ? '#fff' : '#000',
                            backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                        },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: dark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)' },
                    }}
                />
                <IconButton onClick={handleSend} disabled={disabled || !draft.trim()} aria-label={t('online.chat.send')} color="primary">
                    <SendIcon />
                </IconButton>
            </Stack>
        </>
    );
}

/**
 * Shared chat UI for the waiting room (`variant="inline"`, always expanded,
 * embedded in a Card by WaitingRoomScreen.jsx) and in-game (`variant="floating"`,
 * a dropdown panel under the header's chat icon button, over the Pixi
 * canvas). The toggle button lives in Header.jsx (rightmost, so it can never
 * collide with the bottom-anchored in-game action buttons); this component
 * only renders the panel itself, controlled via `open`/`onClose` — open state
 * and the unread badge count are owned by the `useChatUnread` hook in the
 * parent screen and shared with Header. Data comes from useRoomChat — this
 * component is purely presentational.
 */
export default function ChatPanel({ variant, messages, onSend, myUserId, disabled = false, open = false, onClose }) {
    const { t } = useTranslation();

    if (variant === 'inline') {
        return (
            <Box sx={{ p: 2, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('online.chat.title')}
                </Typography>
                <ChatBody messages={messages} onSend={onSend} myUserId={myUserId} disabled={disabled} grow dark={false} />
            </Box>
        );
    }

    return (
        <Fade in={open} unmountOnExit>
            <Box
                sx={{
                    position: 'fixed',
                    top: { xs: 52, sm: 64 },
                    right: { xs: 8, sm: 12 },
                    zIndex: 1000,
                    width: 280,
                    background: 'rgba(0, 0, 0, 0.85)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '12px',
                    padding: '10px 12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {t('online.chat.title')}
                    </Typography>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }} aria-label="close-chat">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <ChatBody messages={messages} onSend={onSend} myUserId={myUserId} disabled={disabled} height={220} />
            </Box>
        </Fade>
    );
}
